import { todayKolkata, validPaymentDay } from '../utils/paymentDates';
import DeliveryPanel from './DeliveryPanel';
import { downloadLatestInvoiceReceipt } from '../services/invoiceReceiptApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toPaise, fromPaise, sumPaise } from '../utils/inr';

export default function RunningPayManage() {
  const [savedInvoice, setSavedInvoice] = useState(null);
  const [deliveryId, setDeliveryId] = useState(null);
  const onDeliveryChanged = useCallback(() => {}, []);
  const [authed, setAuthed] = useState(false);
  const [pwd, setPwd] = useState('');
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // invoice being edited (meta only)

  const inr = (n) => (Number(n || 0)).toLocaleString('en-IN');

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get('/api/running');
      setRows(Array.isArray(r.data) ? r.data : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r => {
      return (
        (r.invoiceNumber || '').toLowerCase().includes(term) ||
        (r.customerName || '').toLowerCase().includes(term) ||
        (r.organization || '').toLowerCase().includes(term) ||
        (r.whatsapp || '').toLowerCase().includes(term)
      );
    });
  }, [rows, q]);

  async function downloadReceipt(doc) {
    try { await downloadLatestInvoiceReceipt(doc); }
    catch (error) { alert(error.response?.data?.error || error.message || 'Unable to generate receipt PDF.'); }
  }

  if (!authed) {
    return (
      <div className="max-w-sm mx-auto mt-16 bg-white p-6 rounded-xl shadow">
        <h2 className="text-xl font-semibold mb-3">Running Pay Manage</h2>
        <p className="text-sm text-gray-600 mb-3">Enter password to continue.</p>
        <input type="password" className="w-full border px-3 py-2 rounded mb-3" placeholder="Password" value={pwd} onChange={e => setPwd(e.target.value)} onKeyDown={(e)=>{ if(e.key==='Enter' && pwd==='admin') setAuthed(true); }} />
        <button className="w-full bg-indigo-600 text-white px-4 py-2 rounded" onClick={()=>{ if(pwd==='admin') setAuthed(true); else alert('Invalid password'); }}>Unlock</button>
        <p className="text-xs text-gray-500 mt-2">(Use password: admin)</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center gap-2 mb-4">
        <input className="border px-3 py-2 rounded flex-1" placeholder="Search by invoice, name, org, WhatsApp" value={q} onChange={e=>setQ(e.target.value)} />
        <button className="bg-gray-100 px-3 py-2 rounded" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl shadow ring-1 ring-gray-100">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Invoice</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Customer</th>
              <th className="px-3 py-2 text-left">Org</th>
              <th className="px-3 py-2 text-left">Mode</th>
              <th className="px-3 py-2 text-right">Total (INR)</th>
              <th className="px-3 py-2 text-right">Received (INR)</th>
              <th className="px-3 py-2 text-right">Due (INR)</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const dt = new Date(r.invoiceDate || r.date || Date.now());
              return (
                <tr key={r._id} className="border-t">
                  <td className="px-3 py-2">{r.invoiceNumber || r._id}</td>
                  <td className="px-3 py-2">{dt.toLocaleDateString('en-IN')}</td>
                  <td className="px-3 py-2">{r.customerName}</td>
                  <td className="px-3 py-2">{r.organization || '-'}</td>
                  <td className="px-3 py-2">{r.paymentMode || '-'}</td>
                  <td className="px-3 py-2 text-right">INR {inr(r.totalAmount)}</td>
                  <td className="px-3 py-2 text-right">INR {inr(r.amountReceived)}</td>
                  <td className="px-3 py-2 text-right">INR {inr(r.balanceDue)}</td>
                  <td className="px-3 py-2">{String(r.status || '').toUpperCase()}</td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button className="text-indigo-600 hover:underline" onClick={()=>{ setSavedInvoice(null); setEditing(r); }}>Edit</button>
                    <button className="text-gray-700 hover:underline" onClick={()=>downloadReceipt(r)}>Receipt</button>
                    <button className="text-rose-600 hover:underline" onClick={async()=>{
                      if (!window.confirm('Delete this invoice permanently?')) return;
                      try {
                        await axios.delete(`/api/running/${r._id}`, { data: { expectedVersion: r.__v ?? 0 } });
                        load();
                      } catch (error) { alert(error.response?.data?.error || 'Unable to delete invoice'); }
                    }}>Delete</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-gray-500">No invoices found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {savedInvoice && <div role="status" className="mt-3 text-sm text-emerald-800">Invoice saved. <button className="border rounded px-3 py-1.5 ml-2" onClick={() => setDeliveryId(savedInvoice._id)}>Open Delivery</button></div>}
      {deliveryId && <DeliveryPanel key={deliveryId} invoiceId={deliveryId} onClose={() => { setDeliveryId(null); load(); }} onChanged={onDeliveryChanged} />}
      {editing && (
        <EditInvoiceModal
          doc={editing}
          onClose={()=>setEditing(null)}
          onSaved={(updated)=>{ setSavedInvoice(updated); setEditing(null); setRows(prev => prev.map(x => x._id === updated._id ? updated : x)); }}
          onRefresh={load}
        />
      )}
    </div>
  );
}

function EditInvoiceModal({ doc, onClose, onSaved, onRefresh }) {
  const [customerName, setCustomerName] = useState(doc.customerName || '');
  const [whatsapp, setWhatsapp] = useState(doc.whatsapp || '');
  const [notes, setNotes] = useState(doc.notes || '');
  const [paymentMode, setPaymentMode] = useState(doc.paymentMode || 'UPI');
  const [organization, setOrganization] = useState(doc.organization || 'COG');
  const storedDate = doc.invoiceDate || doc.date;
  const originalDate = storedDate && Number.isFinite(new Date(storedDate).getTime()) ? new Date(storedDate).toISOString().slice(0, 10) : '';
  const [invoiceDate, setInvoiceDate] = useState(originalDate);

  const [pAmount, setPAmount] = useState(0);
  const [pMode, setPMode] = useState('UPI');
  const [pDate, setPDate] = useState(todayKolkata);
  const inr = (n) => (Number(n || 0)).toLocaleString('en-IN');

  async function handleMutation(action) {
    try { await action(); }
    catch (error) {
      alert(error.response?.data?.error || error.message || 'Unable to save invoice changes. Reload before retrying.');
      if (error.response?.status === 409) { onClose(); onRefresh(); }
    }
  }

  async function saveMeta() {
    const r = await axios.patch(`/api/running/${doc._id}`, { customerName, whatsapp, notes, paymentMode, organization, ...(invoiceDate && invoiceDate !== originalDate ? { invoiceDate } : {}), expectedVersion: doc.__v ?? 0 });
    onSaved(r.data);
  }

  async function addPayment() {
    const add = fromPaise(toPaise(pAmount || 0, 'New payment'));
    if (!(add > 0)) return alert('Enter a positive payment amount');
    if (!validPaymentDay(pDate)) return alert('A valid Payment Date is required');
    const currentReceived = Number(doc.amountReceived || 0);
    if (sumPaise([toPaise(currentReceived), toPaise(add)]) > toPaise(doc.totalAmount)) return alert('Payment exceeds invoice total');
    const r = await axios.post(`/api/running/${doc._id}/payments`, { amount: add, mode: pMode, date: pDate || undefined, expectedVersion: doc.__v ?? 0 });
    onSaved(r.data);
    setPAmount(0); setPDate('');
  }

  async function editPayment(idx) {
    const nv = prompt('New amount (INR)?', String(doc.payments[idx].amount || 0));
    if (nv === null) return;
    const amt = fromPaise(toPaise(nv, 'Payment amount'));
    if (!(amt > 0)) return alert('Amount must be > 0');
    const r = await axios.patch(`/api/running/${doc._id}/payments/${idx}`, { amount: amt, expectedVersion: doc.__v ?? 0 });
    onSaved(r.data);
  }

  async function deletePayment(idx) {
    if (!window.confirm('Delete this payment?')) return;
    const r = await axios.delete(`/api/running/${doc._id}/payments/${idx}`, { data: { expectedVersion: doc.__v ?? 0 } });
    if (r.data?.doc) onSaved(r.data.doc); else onRefresh();
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-end md:items-center justify-center p-2">
      <div className="w-full max-w-3xl bg-white rounded-xl shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Edit Invoice — {doc.invoiceNumber || doc._id}</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-black">✕</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm mb-1">Customer</label>
            <input className="w-full border px-2 py-1 rounded" value={customerName} onChange={e=>setCustomerName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">WhatsApp</label>
            <input className="w-full border px-2 py-1 rounded" value={whatsapp} onChange={e=>setWhatsapp(e.target.value.replace(/[^+\d]/g,''))} />
          </div>
          <div>
            <label className="block text-sm mb-1">Invoice Date</label>
            <input type="date" className="w-full border px-2 py-1 rounded" value={invoiceDate} onChange={e=>setInvoiceDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">Organization</label>
            <select disabled className="w-full border px-2 py-1 rounded" value={organization} onChange={e=>setOrganization(e.target.value)}>
              <option value="COG">COG</option>
              <option value="The Bengal Craft">The Bengal Craft</option>
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Payment Mode</label>
            <select className="w-full border px-2 py-1 rounded" value={paymentMode} onChange={e=>setPaymentMode(e.target.value)}>
              <option value="UPI">UPI</option>
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
              <option value="NetBanking">NetBanking</option>
              <option value="Others">Others</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm mb-1">Notes</label>
            <textarea className="w-full border px-2 py-1 rounded" value={notes} onChange={e=>setNotes(e.target.value)} />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium">Payments</div>
            <div className="text-sm text-gray-500">Total: INR {inr(doc.totalAmount)} · Received: INR {inr(doc.amountReceived)} · Due: INR {inr(doc.balanceDue)}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-gray-600">
                  <th className="px-2 py-1 text-left">Date</th>
                  <th className="px-2 py-1 text-left">Mode</th>
                  <th className="px-2 py-1 text-right">Amount (INR)</th>
                  <th className="px-2 py-1 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(doc.payments || []).map((p, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-2 py-1">{new Date(p.date).toLocaleDateString('en-IN')}</td>
                    <td className="px-2 py-1">{p.mode || '-'}</td>
                    <td className="px-2 py-1 text-right">INR {inr(p.amount)}</td>
                    <td className="px-2 py-1 text-right space-x-2">
                      <button className="text-indigo-600 hover:underline" onClick={()=>handleMutation(()=>editPayment(idx))}>Edit</button>
                      <button className="text-rose-600 hover:underline" onClick={()=>handleMutation(()=>deletePayment(idx))}>Delete</button>
                    </td>
                  </tr>
                ))}
                {(!doc.payments || doc.payments.length===0) && (
                  <tr><td colSpan={4} className="px-2 py-3 text-center text-gray-500">No payments yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-sm mb-1">New Payment (INR)</label>
              <input className="w-full border px-2 py-1 rounded" type="number" value={pAmount} onChange={e=>setPAmount(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm mb-1">Mode</label>
              <select className="w-full border px-2 py-1 rounded" value={pMode} onChange={e=>setPMode(e.target.value)}>
                <option value="UPI">UPI</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="NetBanking">NetBanking</option>
                <option value="Others">Others</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Payment Date</label>
              <input className="w-full border px-2 py-1 rounded" type="date" aria-label="Payment Date" required value={pDate} onChange={e=>setPDate(e.target.value)} />
            </div>
            <div className="flex items-end justify-end gap-2">
              <button className="bg-gray-100 px-3 py-1.5 rounded" onClick={()=>handleMutation(saveMeta)}>Save Changes</button>
              <button className="bg-emerald-600 text-white px-3 py-1.5 rounded" onClick={()=>handleMutation(addPayment)}>Add Payment</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
